import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { TaskInstance } from 'ember-concurrency';
import type HotelPricingManagerBulkUpdateController from 'lumen/controllers/hotel/pricing-manager/bulk-update';
import type { Carecommendation, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { PA_REF_DAYS_AGO } from 'lumen/utils/constants/pricing-assistant-constants';

type BulkUpdateParams = {
  bulkEndDate: string;
  bulkStartDate: string;
};

type BulkUpdateModel = {
  caratesTaskResult: Partial<TaskInstance<Period[] | null>>;
  carecommendationsTaskResult: Partial<TaskInstance<Carecommendation[] | null>>;
};

export default class HotelPricingManagerBulkUpdateRoute extends Route {
  queryParams = {
    bulkStartDate: {
      refreshModel: true,
    },
    bulkEndDate: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare router: Services['router'];

  beforeModel(): void {
    // TODO-BULK-UPDATE: Remove hasPricingAssistantOpenPricingFeature check when ORP is implemented
    if (
      !this.access.hasPricingAssistantEditRights ||
      !this.access.hasPricingAssistantViewRights ||
      this.access.hasPricingAssistantOpenPricingFeature
    ) {
      this.router.transitionTo('hotel.pricing-manager');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: BulkUpdateParams) {
    const { bulkStartDate, bulkEndDate } = params;

    const modelObject: BulkUpdateModel = {
      caratesTaskResult: {},
      carecommendationsTaskResult: {},
    };

    if (!bulkStartDate || !bulkEndDate) {
      return modelObject;
    }

    const hotelId = this.hotelAndMarketService.hotel.id!;
    const compareDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(PA_REF_DAYS_AGO, 'day')
      .format('YYYY-MM-DD');

    modelObject.caratesTaskResult =
      this.pricingAssistantDataRequestService.pricingAssistantRatesRequestTask.perform(
        hotelId,
        bulkStartDate,
        bulkEndDate,
        compareDate,
      );

    modelObject.carecommendationsTaskResult =
      this.pricingAssistantDataRequestService.pricingAssistantRecommendationsRequestTask.perform(
        hotelId,
        bulkStartDate,
        bulkEndDate,
      );

    return modelObject;
  }

  resetController(controller: HotelPricingManagerBulkUpdateController): void {
    controller.bulkStartDate = null;
    controller.bulkEndDate = null;
  }
}
